import { z } from "zod";
import { METRIC_SOURCES } from "../health/metricEvidence";

/**
 * The v2 cloud payload intentionally has no escape hatch for native evidence.
 * These rows are portfolio results, never an archive of a local tracker.
 */
const digest = z.string().regex(/^[a-f0-9]{64}$/);
const uuid = z.string().uuid();
const iso = z.string().datetime({ offset: true });
const bounded = (max: number) => z.string().trim().min(1).max(max);
const nullableBounded = (max: number) => z.string().trim().min(1).max(max).nullable();
const finite = z.number().finite().min(0);
const finiteInt = z.number().int().finite().min(0);

export const resultShapes = {
  measurement_sets: z.object({
    project_id: uuid, id: digest, revision: z.number().int().positive(),
    kind: z.enum(["code_scan", "agent_day", "feedback_day", "clean_repeat", "check_day"]),
    source_domain_id: uuid, device_id: uuid.nullable(), source_ref: digest, revision_ref: digest.nullable(),
    producer_kind: z.enum(['local','github']).nullable().optional(),
    baseline_source_id: uuid.nullable().optional(), baseline_attempt_id: digest.nullable().optional(),
    commit_sha: z.string().regex(/^[a-f0-9]{40}$/).nullable().optional(), environment_digest: digest.nullable().optional(),
    source_kind: z.enum(["committed", "working_tree"]).nullable(), event_at: iso.nullable(),
    input_digest: digest.nullable(), policy_digest: digest.nullable(), profile: nullableBounded(32),
    period_start: iso, period_end: iso, measured_at: iso, harness: nullableBounded(32),
    model: nullableBounded(160), actor_scope: z.enum(["primary", "child", "unknown", "all"]).nullable(),
    context: z.enum(["local", "ci"]).nullable(), cohort_digest: digest.nullable(),
    provenance: z.enum(["observed_forward", "recomputed_historical", "derived_native"]),
    population: z.enum(["analyzer_scoped_inventory", "registered_turns", "recorded_comparable_sessions", "serial_same_revision_checks"]),
    derivation: bounded(80), visibility: z.enum(["visible", "hidden"]), content_digest: digest,
  }).strict(),
  metric_results: z.object({
    project_id: uuid, set_id: digest, set_revision: z.number().int().positive(),
    metric_key: bounded(80), value: z.number().finite().nullable(), unit: bounded(32),
    statistic: z.enum(["value", "count", "median", "sum", "rate"]),
    state: z.enum(["measured", "partial", "unavailable", "not_applicable", "not_requested", "legacy_unverified"]),
    method_id: nullableBounded(200), eligible_count: finiteInt.nullable(), measured_count: finiteInt.nullable(),
    eligible_basis: bounded(32), measured_basis: bounded(32), numerator: finite.nullable(), denominator: finite.nullable(),
    completeness: z.enum(["complete", "partial", "unknown"]),
    reason_code: z.enum(["qualified", "partial_source", "missing_source", "not_requested", "not_applicable", "legacy_unverified", "insufficient_repeats", "coverage_gap"]),
    population: bounded(80),
  }).strict(),
  improvements: z.object({
    project_id: uuid, id: digest, version: z.number().int().positive(), state: bounded(32),
    title: bounded(240), problem: bounded(2000), change_text: bounded(2000), expected_effect: bounded(1000),
    applicability: bounded(1000), metric_label: bounded(500), quality_guardrail: nullableBounded(2000),
    issue_key: nullableBounded(160), identity_digest: digest, created_at: iso, updated_at: iso,
    definition_digest: digest, plan_state: z.enum(["configured", "incomplete"]),
  }).strict(),
  trials: z.object({
    project_id: uuid, id: digest, version: z.number().int().positive(), improvement_id: digest.nullable(),
    improvement_version: z.number().int().positive().nullable(), title: bounded(240), hypothesis: bounded(2000),
    state: z.enum(["active", "closed"]), metric_key: nullableBounded(500),
    mode: z.enum(["attempts", "operation", "code", "legacy"]), direction: z.enum(["increase", "decrease"]).nullable(), unit: nullableBounded(80),
    definition_digest: digest, definition_text: nullableBounded(2000), eligibility_text: nullableBounded(2000),
    quality_guardrail: nullableBounded(2000), stop_rule: nullableBounded(2000), minimum_evidence: finiteInt.nullable(),
    practical_threshold: finite.nullable(), harness: nullableBounded(32), model: nullableBounded(160),
    actor_scope: z.enum(["primary", "all"]).nullable(), area_codes: z.array(bounded(80)).max(8),
    recipe_digest: digest.nullable(), dataset_digest: digest.nullable(), started_at: iso, implemented_at: iso.nullable(),
    finished_at: iso.nullable(), baseline_from: iso.nullable(), baseline_to: iso.nullable(), followup_from: iso.nullable(), followup_to: iso.nullable(),
    decision: z.enum(["keep", "revert", "more-evidence"]).nullable(), readiness: z.enum(["collecting", "incompatible", "regression", "ready"]),
    baseline_count: finiteInt, followup_count: finiteInt, baseline_value: z.number().finite().nullable(), followup_value: z.number().finite().nullable(),
    quality_state: z.enum(["passed", "failed", "unknown"]), evidence_digest: digest.nullable(), summary_derivation: bounded(80),
  }).strict(),
  lessons: z.object({
    project_id: uuid, id: digest, version: z.number().int().positive(), improvement_id: digest.nullable(), state: bounded(32),
    guidance: bounded(2000), rationale: bounded(2000), applicability: bounded(1000), reviewed_at: iso.nullable(),
  }).strict(),
  learning_observations: z.object({
    project_id: uuid, id: digest, attempt_ref: digest, item_kind: z.enum(["lesson", "proposal"]), item_id: digest,
    item_version: z.number().int().positive(), trial_id: digest.nullable(), definition_digest: digest.nullable(),
    phase: z.enum(["baseline", "followup", "transition"]).nullable(), delivered: z.boolean(),
    disposition: z.enum(["used", "not_applicable", "declined", "unknown"]), issue: z.enum(["present", "absent", "unknown"]),
    quality: z.enum(["passed", "failed", "unknown"]), harness: bounded(32), model: nullableBounded(160),
    started_at: iso, closed_at: iso.nullable(), provenance: z.enum(["delivery_receipt", "agent_reported", "operation_receipt"]),
  }).strict(),
};

export type ResultsBundle = { [K in keyof typeof resultShapes]: z.infer<(typeof resultShapes)[K]>[] };
const nativeMetricKeys = new Set(Object.keys(METRIC_SOURCES));
const derivedMetricKey = /^(agent\.(turns|comparableTurns|runtimeMedian|toolsTotal|tokensMedian)|feedback\.(median|partialSessions|unknownSessions)|checks\.(disagreement|eligibleRepeats|insufficientGroups)|findings\.(cloc|fallow|knip|oxlint|compiler|depcruise|react-doctor|qlty|unknown)\.(complexity|duplication|cycle|unused|diagnostics|graph|smell|functions|inventory|unknown)\.(error|warning|info|critical|high|medium|low|unknown))$/;

export function canonicalResultJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalResultJson).join(",")}]`;
  if (value && typeof value === "object") {
    const row = value as Record<string, unknown>;
    return `{${Object.keys(row).sort().map(key => `${JSON.stringify(key)}:${canonicalResultJson(row[key])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

export function decodeResultsBundle(input: unknown): ResultsBundle {
  const body = z.object(Object.fromEntries(Object.keys(resultShapes).map(key => [key, z.array(resultShapes[key as keyof typeof resultShapes])])) as any).strict().parse(input) as ResultsBundle;
  const dictionary = new Map<string, string>();
  const sets = new Map(body.measurement_sets.map(row => [`${row.project_id}:${row.id}:${row.revision}`, row]));
  for (const metric of body.metric_results) {
    if (!nativeMetricKeys.has(metric.metric_key) && metric.metric_key!=='ci.scanDuration' && !derivedMetricKey.test(metric.metric_key)) throw new Error("unregistered_result_metric");
    if (!sets.has(`${metric.project_id}:${metric.set_id}:${metric.set_revision}`)) throw new Error("result_metric_without_set");
    const prior = dictionary.get(metric.metric_key);
    if (prior && prior !== metric.unit) throw new Error("result_metric_unit_conflict");
    dictionary.set(metric.metric_key, metric.unit);
  }
  return body;
}
